import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Router } from '@angular/router'
import { BuilderService } from '../builder-services/builder.service'
import { DomainService } from './services/domain.service'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { environment } from '../../environments/environment'
import { Location } from '@angular/common'
import { ToastrService } from 'ngx-toastr'

@Component({
  selector: 'app-domain',
  templateUrl: './domain.component.html',
  styleUrls: ['./domain.component.scss'],
})
export class DomainComponent implements OnInit {
  // Form Groups
  subdomainForm: FormGroup
  customDomainForm: FormGroup

  // State Variables
  subdomainAvailable: boolean | null = null
  checkingAvailability = false
  savingSubdomain = false
  verifyingDomain = false
  savingCustomDomain = false
  domainSettings: any = {}

// Validation States
  subdomainErrors: string[] = []
  customDomainErrors: string[] = []
  suggestedSubdomain = ''
  dnsInstructions: any[] = []
  customDomainVerified = false
  domainSetupDone = false
  checkingDomainStatus = false
  domainStatus = ''

  // Project ID
  projectId: string

  // Track the last saved subdomain to detect changes
  savedSubdomain = ''

  // Domain suffix from environment
  sitesDomain = environment.sitesDomain

  constructor(
    private fb: FormBuilder,
    private domainService: DomainService,
    public builderService: BuilderService,
    private router: Router,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private location: Location,
    private toastr: ToastrService
  ) {
    this.projectId = this.builderService.selectedProject

    // Initialize forms
    this.subdomainForm = this.fb.group({
      subdomain: ['', [
        Validators.required,
        Validators.pattern(/^[a-z0-9-]+$/),
        Validators.minLength(3),
        Validators.maxLength(63)
      ]]
    })

    this.customDomainForm = this.fb.group({
      domain: ['', [
        Validators.required,
        Validators.pattern(/^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/)
      ]]
    })
  }

  get canUseCustomDomain(): boolean {
    return this.builderService.is_premium
  }

  ngOnInit(): void {
    this.loadDomainSettings()
    this.setupSubdomainChangeListener()
  }

  /**
   * Load domain settings from Firebase
   */
  loadDomainSettings(): void {
    this.builderService.showUpdateProgress = true

    this.domainService.getDomainSettings(this.projectId).subscribe(
      (settings) => {
        this.domainSettings = settings

        // Populate forms with existing data
        if (settings.subdomain) {
          this.subdomainForm.patchValue({
            subdomain: settings.subdomain
          })
          this.savedSubdomain = settings.subdomain
        }

        if (settings.custom_domain) {
          this.customDomainForm.patchValue({
            domain: settings.custom_domain
          })
          this.customDomainVerified = settings.custom_domain_verified || false
        }

        this.builderService.showUpdateProgress = false
        this.checkSubscriptionStatus()
      },
      (error) => {
        console.error('Error loading domain settings:', error)
        this.builderService.showUpdateProgress = false
      }
    )
  }

  /**
   * Check subscription status for custom domain access
   */
  checkSubscriptionStatus(): void {
    if (this.canUseCustomDomain) {
      this.customDomainForm.enable()
    } else {
      this.customDomainForm.disable()
    }

    // Show warning if they had custom domain but subscription downgraded
    if (this.domainSettings.custom_domain && !this.canUseCustomDomain) {
      this.showWarningSnackbar(
        'Custom domain feature requires a premium subscription. ' +
        'Your domain is saved but not active. Upgrade to reactivate.'
      )
    }
  }

  /**
   * Setup real-time subdomain availability checking
   */
  setupSubdomainChangeListener(): void {
    this.subdomainForm.get('subdomain')?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((value) => {
        if (this.subdomainForm.get('subdomain')?.valid) {
          this.checkSubdomainAvailability()
        } else {
          this.subdomainAvailable = null
          this.validateSubdomainFormat(value)
        }
      })
  }

  /**
   * Validate subdomain format and show errors
   */
  validateSubdomainFormat(subdomain: string): void {
    this.subdomainErrors = []

    if (!subdomain) {
      return
    }

    if (!/^[a-z0-9-]+$/.test(subdomain)) {
      this.subdomainErrors.push('Only lowercase letters, numbers, and hyphens allowed')
    }
    if (subdomain.length < 3) {
      this.subdomainErrors.push('Minimum 3 characters required')
    }
    if (subdomain.length > 63) {
      this.subdomainErrors.push('Maximum 63 characters allowed')
    }
    if (/^-|-$/.test(subdomain)) {
      this.subdomainErrors.push('Cannot start or end with hyphen')
    }
    if (/--/.test(subdomain)) {
      this.subdomainErrors.push('No consecutive hyphens allowed')
    }
  }

  /**
   * Check if subdomain is available
   */
  checkSubdomainAvailability(): void {
    const subdomain = this.subdomainForm.value.subdomain

    if (!subdomain || this.subdomainForm.get('subdomain')?.invalid) {
      return
    }

    this.checkingAvailability = true
    this.subdomainAvailable = null
    this.suggestedSubdomain = ''

    this.domainService.checkSubdomainAvailability(subdomain, this.projectId).subscribe(
      (result) => {
        this.subdomainAvailable = result.available
        if (!result.available && result.suggestion) {
          this.suggestedSubdomain = result.suggestion
        }
        this.checkingAvailability = false
      },
      (error) => {
        this.checkingAvailability = false
        this.subdomainErrors.push('Error checking availability')
        console.error('Error checking subdomain:', error)
      }
    )
  }

  /**
   * Use suggested subdomain
   */
  useSuggestion(): void {
    if (this.suggestedSubdomain) {
      this.subdomainForm.patchValue({
        subdomain: this.suggestedSubdomain
      })
      this.suggestedSubdomain = ''
    }
  }

  /**
   * Save subdomain
   */
  saveSubdomain(): void {
    if (this.subdomainForm.invalid || this.subdomainAvailable !== true) {
      this.showErrorSnackbar('Please enter a valid and available subdomain')
      return
    }

    const subdomain = this.subdomainForm.value.subdomain
    this.savingSubdomain = true

    this.domainService.saveSubdomain(this.projectId, subdomain).subscribe(
      (response) => {
        this.savingSubdomain = false
        this.savedSubdomain = subdomain
        this.domainSettings.subdomain = subdomain
        this.domainSettings.subdomain_full = response.subdomain_full
        this.showSuccessSnackbar(`Subdomain saved: ${response.subdomain_full}`)
      },
      (error) => {
        this.savingSubdomain = false
        this.showErrorSnackbar('Failed to save subdomain. Please try again.')
        console.error('Error saving subdomain:', error)
      }
    )
  }

  goBack() {
    this.location.back();
  }

  /**
   * Validate custom domain input
   */
  onDomainInput(): void {
    const domain = this.customDomainForm.value.domain

    if (!domain) {
      this.customDomainErrors = []
      return
    }

    if (!this.domainService.validateDomainFormat(domain)) {
      this.customDomainErrors = [
        'Invalid domain format (Example: mystore.com or shop.mysite.com)'
      ]
    } else {
      this.customDomainErrors = []
    }
  }

  /**
   * Verify custom domain DNS
   */
  verifyCustomDomain(): void {
    if (this.customDomainForm.invalid) {
      this.showErrorSnackbar('Please enter a valid domain')
      return
    }

    const domain = this.customDomainForm.value.domain
    this.verifyingDomain = true
    this.customDomainVerified = false
    this.domainSetupDone = false

    this.domainService.verifyDomainDNS(this.projectId, domain).subscribe(
      (result) => {
        this.verifyingDomain = false

        if (result.verified) {
          this.domainSetupDone = true
          this.dnsInstructions = result.dns_records || []

          if (result.reachable) {
            this.customDomainVerified = true
            this.domainStatus = 'active'
            this.dnsInstructions = []
            this.showSuccessSnackbar('Domain is verified and pointing to our server! You can now save it.')
          } else {
            this.domainStatus = 'pending'
            this.showSuccessSnackbar('Please add the DNS records shown below at your domain registrar.')
          }
        } else {
          this.domainSetupDone = false
          this.showErrorSnackbar(result.error || 'Failed to setup domain.')
        }
      },
      (error) => {
        this.verifyingDomain = false
        this.customDomainVerified = false
        this.domainSetupDone = false
        this.showErrorSnackbar('Failed to setup domain. Please try again.')
        console.error('Error setting up domain:', error)
      }
    )
  }

  checkDomainStatus(): void {
    if (this.customDomainForm.invalid) {
      return
    }

    const domain = this.customDomainForm.value.domain
    this.checkingDomainStatus = true

    this.domainService.checkDomainStatus(this.projectId, domain).subscribe(
      (result) => {
        this.checkingDomainStatus = false

        if (result.verified && result.reachable) {
          this.customDomainVerified = true
          this.domainStatus = 'active'
          this.dnsInstructions = []
          this.showSuccessSnackbar('Domain is active and verified! You can now save it.')
        } else if (result.verified) {
          this.domainStatus = 'pending'
          this.dnsInstructions = result.dns_records || []
          this.showWarningSnackbar(result.message || 'Nameservers not yet propagated. This can take up to 24-48 hours.')
        } else {
          this.domainStatus = 'not_found'
          this.showErrorSnackbar(result.error || 'Domain not found. Please set up the domain first.')
        }
      },
      (error) => {
        this.checkingDomainStatus = false
        this.showErrorSnackbar('Failed to check domain status.')
        console.error('Error checking domain status:', error)
      }
    )
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.showSuccessSnackbar('Copied to clipboard!')
    })
  }

  /**
   * Save custom domain
   */
  saveCustomDomain(): void {
    if (this.customDomainForm.invalid || !this.customDomainVerified) {
      this.showErrorSnackbar('Please verify the domain first')
      return
    }

    const domain = this.customDomainForm.value.domain
    this.savingCustomDomain = true

    this.domainService.saveCustomDomain(this.projectId, domain, true).subscribe(
      (response) => {
        this.savingCustomDomain = false
        this.domainSettings.custom_domain = domain
        this.domainSettings.custom_domain_verified = true
        this.showSuccessSnackbar('Custom domain saved successfully!')
      },
      (error) => {
        this.savingCustomDomain = false
        this.showErrorSnackbar('Failed to save custom domain. Please try again.')
        console.error('Error saving custom domain:', error)
      }
    )
  }

  /**
   * Show upgrade dialog for premium features
   */
  showUpgradeDialog(): void {
    const baseUrl = `${environment.stacksURL}pricing`;
    if (this.projectId) {
      window.open(`${baseUrl}?project_id=${encodeURIComponent(this.projectId)}`, '_blank');
    } else {
      window.open(baseUrl, '_blank');
    }
  }
  /**
   * Preview website with current domain
   */
  previewWebsite(): void {
    const previewUrl = this.domainService.getWebsiteUrl(this.projectId, 'staging')
    window.open(previewUrl, '_blank')
  }

  /**
   * Get current website URL
   */
  getCurrentWebsiteUrl(): string {
    return this.domainService.getWebsiteUrl(this.projectId, 'prod')
  }

  /**
   * Show success message
   */
  private showSuccessSnackbar(message: string): void {
    this.toastr.success(message)
  }

    /**
     * Show error message
     */
  private showErrorSnackbar(message: string): void {
    this.toastr.error(message)
  }

    /**
     * Show warning message
     */
  private showWarningSnackbar(message: string): void {
    this.toastr.warning(message)
  }
}
